"use client";

import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { ChevronDown } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { Button } from "@/components/atoms/Button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/molecules/Accordion";
import Dropdown from "@/components/molecules/Dropdown";
interface MenuItem {
  id: number;
  title: string;
  slug: string;
  url?: string;
  priority: number;
  status: boolean;
  icon?: string;
  text_color?: string;
  is_button: boolean;
  button_color?: string;
  children?: MenuItem[];
}

interface NestedRouteItem {
  id: number;
  label: string;
  route: string;
  textColor?: string;
}

interface RouteItem {
  id: number;
  route: string;
  label: string;
  type?: string;
  nestedRoutes?: NestedRouteItem[];
  isButton?: boolean;
  buttonColor?: string;
  textColor?: string;
}

/** True when the menu path matches the current pathname (with or without a leading slash). */
function pathsMatch(routePath: string, currentPath: string): boolean {
  const normalized = routePath.startsWith("/") ? routePath : `/${routePath}`;
  return routePath === currentPath || normalized === currentPath;
}

/** Dropdown parents often use "/" or "" as a placeholder when the real links are only in children. */
function isRootPlaceholderParentRoute(routePath: string): boolean {
  const t = routePath?.trim() ?? "";
  return t === "" || t === "/";
}

function isDropdownParentActive(route: RouteItem, pathname: string): boolean {
  if (!route.nestedRoutes?.length) return false;
  const onRoot = pathname === "/" || pathname === "";
  if (
    pathsMatch(route.route, pathname) &&
    !(onRoot && isRootPlaceholderParentRoute(route.route))
  ) {
    return true;
  }
  return route.nestedRoutes.some((n) => pathsMatch(n.route, pathname));
}

const NavBar1 = ({ data }: { data: any }) => {
  const { data: sectionData, isPreview = false } = data;
  const pathname = usePathname();
  const dispatch = useDispatch();
  if (!sectionData) {
    return null;
  }

  const {
    background_color,
    menuItems = [],
    hamburger_icon = "",
    mobile_logo_icon = "",
    mobile_logo_icon_alt = "",
    show_hamburger,
  } = sectionData;

  // Hamburger visibility. Defaults TRUE — a missing or NULL value must keep
  // the hamburger, so only an explicit editor opt-out ever hides it.
  const showHamburger = !(
    show_hamburger === false ||
    show_hamburger === 0 ||
    show_hamburger === "0"
  );

  // Transform menuItems from backend to routes format
  const routes: RouteItem[] = useMemo(() => {
    if (!menuItems || menuItems.length === 0) {
      console.log("NavBar: No menu items found");
      return [];
    }

    // Filter active items and sort by priority
    const activeItems = menuItems
      .filter((item: MenuItem) => item.status)
      .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority);

    const transformedRoutes = activeItems.map((item: MenuItem) => {
      // Use URL if provided and not empty, otherwise fallback to slug
      const routePath =
        item.url && item.url.trim() !== "" ? item.url : `/${item.slug}`;
      const route: RouteItem = {
        id: item.id,
        route: routePath,
        label: item.title,
        type: "header",
        isButton: Boolean(item.is_button), // Convert number to boolean
        buttonColor: item.button_color,
        textColor: item.text_color,
      };

      // If item has children, add them as nestedRoutes
      if (item.children && item.children.length > 0) {
        const activeChildren = item.children
          .filter((child: MenuItem) => child.status)
          .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority);

        route.nestedRoutes = activeChildren.map((child: MenuItem) => ({
          id: child.id,
          label: child.title,
          route:
            child.url && child.url.trim() !== "" ? child.url : `/${child.slug}`,
          textColor: child.text_color || item.text_color,
        }));
      }

      return route;
    });

    return transformedRoutes;
  }, [menuItems]);

  // Separate button items from regular menu items
  const menuRoutes = routes;

  const activePath = menuRoutes.find(
    (route) => route.route === pathname,
  )?.route;

  if (routes.length === 0) {
    return null;
  }

  const buttonItem = routes.find((route) => route.isButton);

  return (
    <>
      {/* Mobile view - shown on xs (mobile), hidden on sm and up (desktop) */}
      <div
        className="sticky top-[24px] left-0 right-0 z-[45] items-center justify-between w-full px-2 py-4 xs:flex sm:hidden"
        style={{ backgroundColor: background_color || "#000000" }}
      >
        <div className="flex items-center gap-2">
          {/* The Dropdown IS the hamburger (trigger + drawer) — the whole
              thing goes when the toggle is off. */}
          {showHamburger && (
          <Dropdown
            contentClassName="py-[18px] pr-3 pl-[30px] w-[252px] max-h-[calc(100svh-8rem)] overflow-y-auto overflow-x-hidden overscroll-y-contain shadow-[0_-2px_14px_-2px_rgba(0,0,0,0.045),0_6px_20px_-4px_rgba(0,0,0,0.08)]"
            contentStyle={{ backgroundColor: background_color || "#ffffff" }}
            triggerLabel={
              hamburger_icon && (
                <Image
                  src={hamburger_icon || ""}
                  alt="hamburger"
                  width={32}
                  height={32}
                />
              )
            }
          >
            <div className="flex flex-col gap-[18px]">
              {menuRoutes.map((route) => {
                if (route.nestedRoutes) {
                  return (
                    <Accordion
                      key={route.id}
                      type="single"
                      collapsible
                      className="!z-1"
                    >
                      <AccordionItem key={route.id} value={`nav-${route.id}`}>
                        <AccordionTrigger className="!text-heading-9-semibold !font-semibold text-left [&>svg]:shrink-0">
                          <div
                            className="w-full max-w-[198px] whitespace-normal break-words pr-1 type-h6"
                            style={{ color: route.textColor || "#000000" }}
                          >
                            {route.label}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pl-3 flex flex-col gap-[18px] ">
                          {route.nestedRoutes.map((nestedRoute) => (
                            <Link
                              className="text-heading-9-semibold block w-full max-w-[198px] whitespace-normal break-words"
                              key={nestedRoute.id}
                              href={nestedRoute.route}
                              style={{
                                color:
                                  nestedRoute.textColor ||
                                  route.textColor ||
                                  "#000000",
                              }}
                            >
                              {nestedRoute.label}
                            </Link>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  );
                }

                if (route.isButton) {
                  return null;
                }

                return (
                  <Link
                    className="text-heading-9-semibold"
                    key={route.id}
                    href={route.route}
                    style={{
                      color: route.textColor || "#000000",
                    }}
                  >
                    {route.label}
                  </Link>
                );
              })}
            </div>
          </Dropdown>
          )}
          <div className="w-[96px] h-[40px]">
            {mobile_logo_icon && (
              <Image
                className="w-full h-full object-fill"
                src={mobile_logo_icon || ""}
                alt={mobile_logo_icon_alt || "logo"}
                width={100}
                height={100}
              />
            )}
          </div>
        </div>
        {buttonItem && (
          <Button
            onClick={() =>
              dispatch(
                handleOpenLeaddrop({
                  variant: LEAD_DROP_VARIANTS.REGISTER,
                }),
              )
            }
            classType="default"
            style={{
              backgroundColor: buttonItem.buttonColor || "#BE2330",
              color: buttonItem.textColor || "#FFFFFF",
            }}
          >
            {buttonItem.label}jj
          </Button>
        )}
      </div>
      {/* Desktop view - hidden on xs (mobile), shown on sm and up (desktop) */}
      <div
        style={{ backgroundColor: background_color }}
        className={cn(
          "shadow-3 rounded-lg items-center w-max hidden sm:flex",
          isPreview
            ? "relative mx-auto sm:px-3 sm:py-3 sm:gap-7 md:gap-[38px] md:px-4 lg:gap-12 lg:px-[18px] xl:gap-12 xl:px-[18px]"
            : "fixed top-26 left-[50%] -translate-x-1/2 z-50 sm:px-3 sm:top-20 sm:py-[4.5px] sm:gap-7 md:gap-[38px] md:top-24 md:px-4 md:py-[6px] lg:gap-12 lg:top-28 lg:px-[18px] lg:py-[8px] xl:gap-12 xl:px-[18px] xl:py-[10px]",
        )}
      >
        <div
          className={cn(
            "flex items-center",
            "sm:gap-7",
            "md:gap-[38px]",
            "lg:gap-12",
            "xl:gap-12",
          )}
        >
          {menuRoutes.map((route) => {
            if (route.nestedRoutes) {
              const dropdownActive = isDropdownParentActive(route, pathname);
              return (
                <Dropdown
                  key={route.id}
                  contentStyle={{ backgroundColor: background_color }}
                  triggerLabel={
                    <div
                      className={cn(
                        "flex flex-col items-center gap-[2px]",
                        "sm:text-heading-9-semibold",
                        "md:text-heading-8-semibold",
                        "lg:text-heading-7-semibold",
                        "xl:text-heading-6-semibold",
                        {
                          "text-secondary-main": dropdownActive,
                        },
                      )}
                      style={{
                        color: dropdownActive
                          ? undefined
                          : route.textColor || "#000000",
                        backgroundColor: background_color,
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <div className="max-w-[190px] cursor-pointer line-clamp-1 type-h6">
                          {route.label}
                        </div>
                        <ChevronDown width={15} height={15} />
                      </div>

                      {dropdownActive && (
                        <div className="w-[60%] h-1 bg-secondary-main rounded-full type-h6" />
                      )}
                    </div>
                  }
                >
                  {route.nestedRoutes.map((nestedRoute) => {
                    const nestedActive = pathsMatch(
                      nestedRoute.route,
                      pathname,
                    );
                    return (
                      <Link
                        key={nestedRoute.id}
                        href={nestedRoute.route}
                        className={cn(
                          "py-1.5",
                          "sm:text-heading-9-medium",
                          "md:text-heading-8-medium",
                          "lg:text-heading-7-medium",
                          "xl:text-heading-6-medium",
                          "type-h6",
                          {
                            "text-secondary-main": nestedActive,
                          },
                        )}
                        style={{
                          color: nestedActive
                            ? undefined
                            : nestedRoute.textColor ||
                              route.textColor ||
                              "#000000",
                        }}
                      >
                        {nestedRoute.label}
                      </Link>
                    );
                  })}
                </Dropdown>
              );
            }

            if (route.isButton) {
              return (
                <Button
                  key={route.id}
                  onClick={() =>
                    dispatch(
                      handleOpenLeaddrop({
                        variant: LEAD_DROP_VARIANTS.REGISTER,
                      }),
                    )
                  }
                  className={cn(
                    "class-btn",
                    "sm:text-heading-9-semibold",
                    "md:text-heading-8-semibold",
                    "lg:text-heading-7-semibold",
                    "xl:text-heading-6-semibold type-h6",
                  )}
                  style={{
                    backgroundColor: route.buttonColor || "#BE2330",
                    color: route.textColor || "#FFFFFF",
                  }}
                >
                  {route.label}
                </Button>
              );
            }
            return (
              <Link
                key={route.id}
                href={route.route}
                className={cn(
                  "flex flex-col items-center gap-[2px]",
                  "sm:text-heading-9-semibold",
                  "md:text-heading-8-semibold",
                  "lg:text-heading-7-semibold",
                  "xl:text-heading-6-semibold",
                  "type-h6",
                  {
                    "text-secondary-main": activePath === route.route,
                  },
                )}
                style={{
                  color:
                    activePath === route.route
                      ? undefined
                      : route.textColor || "#000000",
                }}
              >
                {route.label}

                {activePath === route.route && (
                  <div className="w-[60%] h-1 bg-secondary-main rounded-full" />
                )}
              </Link>
            );
          })}
        </div>
        {/* {buttonItem && (
        <Button
          onClick={() =>
            dispatch(
              handleOpenLeaddrop({
                variant: LEAD_DROP_VARIANTS.REGISTER,
              })
            )
          }
          className={cn(
            "class-btn",
            "sm:text-heading-9-semibold",
            "md:text-heading-8-semibold",
            "lg:text-heading-7-semibold",
            "xl:text-heading-6-semibold"
          )}
          style={{
            backgroundColor: buttonItem.buttonColor || "#BE2330",
            color: buttonItem.textColor || "#FFFFFF",
          }}
        >
          {buttonItem.label}
        </Button>
      )} */}
      </div>
    </>
  );
};

export default NavBar1;
